/**
 * article-modular.js
 * JavaScript for the Modular Article Section only.
 * Replaces blog-page.js dependency for this section.
 *
 * Includes:
 *  1. Modular Article TOC (mobile accordion + desktop sidebar)
 *  2. Why Trust Us accordion toggle
 *  3. Empty paragraph / inline element suppression
 */

document.addEventListener('DOMContentLoaded', () => {

  // ============================================================
  // Mark body as article page for CSS scoping
  // (hides .st-search-box.hidden-desktop and verifast on mobile)
  // ============================================================
  document.body.classList.add('is-article-page');

  // ============================================================
  // Helper: Smooth Slide Toggle
  // ============================================================
  function slideToggle(element, duration = 300) {
    if (window.getComputedStyle(element).display === 'none') {
      // slideDown
      element.style.removeProperty('display');
      let display = window.getComputedStyle(element).display;
      if (display === 'none') display = 'block';
      element.style.display = display;
      let height = element.offsetHeight;
      element.style.overflow = 'hidden';
      element.style.height = 0;
      element.style.paddingTop = 0;
      element.style.paddingBottom = 0;
      element.style.marginTop = 0;
      element.style.marginBottom = 0;
      element.offsetHeight; // reflow
      element.style.transitionProperty = "height, margin, padding";
      element.style.transitionDuration = duration + 'ms';
      element.style.transitionTimingFunction = "ease-in-out";
      element.style.height = height + 'px';
      element.style.removeProperty('padding-top');
      element.style.removeProperty('padding-bottom');
      element.style.removeProperty('margin-top');
      element.style.removeProperty('margin-bottom');
      window.setTimeout(() => {
        element.style.removeProperty('height');
        element.style.removeProperty('overflow');
        element.style.removeProperty('transition-duration');
        element.style.removeProperty('transition-property');
        element.style.removeProperty('transition-timing-function');
      }, duration);
    } else {
      // slideUp
      element.style.transitionProperty = 'height, margin, padding';
      element.style.transitionDuration = duration + 'ms';
      element.style.transitionTimingFunction = "ease-in-out";
      element.style.boxSizing = 'border-box';
      element.style.height = element.offsetHeight + 'px';
      element.offsetHeight; // reflow
      element.style.overflow = 'hidden';
      element.style.height = 0;
      element.style.paddingTop = 0;
      element.style.paddingBottom = 0;
      element.style.marginTop = 0;
      element.style.marginBottom = 0;
      window.setTimeout(() => {
        element.style.display = 'none';
        element.style.removeProperty('height');
        element.style.removeProperty('padding-top');
        element.style.removeProperty('padding-bottom');
        element.style.removeProperty('margin-top');
        element.style.removeProperty('margin-bottom');
        element.style.removeProperty('overflow');
        element.style.removeProperty('transition-duration');
        element.style.removeProperty('transition-property');
        element.style.transitionTimingFunction = "ease-in-out";
      }, duration);
    }
  }

  // ============================================================
  // 1. MODULAR ARTICLE TOC
  //    Populates both mobile (accordion) and desktop (sidebar)
  //    lists from .prose h2/h3 headings found on the page.
  // ============================================================
  function buildArticleTOC() {
    const headings       = document.querySelectorAll('.prose h2, .prose h3, .prose h4, .prose h5, .prose h6');
    const mobileTocEl    = document.getElementById('mobile-toc-accordion');
    const mobileTocList  = document.getElementById('mobile-toc-list');
    const desktopTocEl   = document.getElementById('desktop-toc');
    const desktopTocList = document.getElementById('desktop-toc-list');

    if (!headings.length) {
      if (mobileTocEl)  mobileTocEl.style.display  = 'none';
      if (desktopTocEl) desktopTocEl.style.display = 'none';
      return;
    }
    if (!mobileTocList && !desktopTocList) return;

    const mobileFragment  = document.createDocumentFragment();
    const desktopFragment = document.createDocumentFragment();

    headings.forEach((heading, index) => {
      const text = heading.textContent.trim();
      if (!text) return;

      // #5 — Exclude "Did you know?" callout headings from the TOC
      if (/^did you know/i.test(text)) return;

      const id = `toc-heading-${index}`;
      heading.id = id;

      function onTocLinkClick(e) {
        e.preventDefault();
        const target = document.getElementById(id);
        if (target) {
          const isMobile = window.innerWidth <= 1023;
          
          if (!isMobile) {
            // Desktop: native scrollIntoView works perfectly (sidebar doesn't collapse)
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            // Mobile: calculate exact position compensating for the TOC accordion collapsing!
            const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 60;
            
            // Get height of the mobile TOC that is about to close
            const mobileContent = document.querySelector('.article-toc--mobile__content');
            let tocHeight = 0;
            if (mobileContent && window.getComputedStyle(mobileContent).display !== 'none') {
              tocHeight = mobileContent.offsetHeight;
            }
            
            // Offset = Header Height + The space the TOC will lose when it closes + 20px buffer
            const offset = headerHeight + tocHeight + 90; 
            
            const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = targetPosition - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }

        // Close mobile accordion after navigation
        const toggle  = document.querySelector('.article-toc--mobile__toggle');
        const content = document.getElementById('mobile-toc-content');
        if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
          toggle.setAttribute('aria-expanded', 'false');
          if (content) slideToggle(content, 300);
        }
      }

      if (mobileTocList) {
        const li = document.createElement('li');
        const a  = document.createElement('a');
        a.textContent = text;
        a.href = `#${id}`;
        a.addEventListener('click', onTocLinkClick);
        li.appendChild(a);
        mobileFragment.appendChild(li);
      }

      if (desktopTocList) {
        const li = document.createElement('li');
        const a  = document.createElement('a');
        a.textContent = text;
        a.href = `#${id}`;
        a.addEventListener('click', onTocLinkClick);
        li.appendChild(a);
        desktopFragment.appendChild(li);
      }
    });

    if (mobileTocList)  mobileTocList.appendChild(mobileFragment);
    if (desktopTocList) desktopTocList.appendChild(desktopFragment);

    if (mobileTocEl) mobileTocEl.style.display = '';

    // Wire mobile accordion toggle
    const mobileToggle  = document.querySelector('.article-toc--mobile__toggle');
    const mobileContent = document.getElementById('mobile-toc-content');
    if (mobileToggle && mobileContent) {
      mobileToggle.addEventListener('click', () => {
        const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
        mobileToggle.setAttribute('aria-expanded', String(!expanded));
        slideToggle(mobileContent, 300);
      });
    }
  }

  buildArticleTOC();


  // ============================================================
  // 2. WHY TRUST US — accordion toggle
  //    Also toggles .is-open on the container so the stats panel
  //    (which now lives outside .why-trust-content) can be shown
  //    via the CSS rule .why-trust-accordion-container.is-open .why-trust-stats
  // ============================================================
  document.querySelectorAll('.why-trust-toggle').forEach(whyTrustToggle => {
    whyTrustToggle.addEventListener('click', () => {
      const expanded = whyTrustToggle.getAttribute('aria-expanded') === 'true';
      whyTrustToggle.setAttribute('aria-expanded', String(!expanded));
      const container = whyTrustToggle.closest('.why-trust-accordion-container');
      const content = whyTrustToggle.nextElementSibling;
      if (content) slideToggle(content, 300);
      // Toggle stats visibility (stats panel sits outside the collapsible)
      if (container) container.classList.toggle('is-open', !expanded);
    });
  });


  // ============================================================
  // 3. SUPPRESS EMPTY PARAGRAPHS — &nbsp; / lone <br> rows
  // ============================================================
  document.querySelectorAll('p').forEach(p => {
    const html = p.innerHTML.trim();
    const text = p.textContent.trim();
    if (html === '&nbsp;' || text === '\u00A0') { p.style.display = 'none'; return; }
    if (p.getAttribute('dir') === 'ltr' && html === '<br>') { p.style.display = 'none'; }
  });

});


// ============================================================
// 4. SUPPRESS EMPTY INLINE ELEMENTS (runs immediately)
// ============================================================
document.querySelectorAll(
  '.article-content .prose h1, .article-content .prose h2, .article-content .prose h3, ' +
  '.article-content .prose h4, .article-content .prose h5, .article-content .prose h6, ' +
  '.article-content .prose p, .article-content .prose span'
).forEach(el => {
  if (!el.textContent.trim()) el.style.display = 'none';
});